import { inject } from '@angular/core';
import {
  type ActivatedRouteSnapshot,
  type ResolveFn,
  Router,
} from '@angular/router';
import { AiAssistantV2Store } from '@bizzdesign/ai-assistant/data-access';
import { type UUID, isUUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type AdhocFrameBase,
  type FrameBase,
  FrameHasNoWorkspaceError,
  FrameNotFoundError,
  type InvalidAssetIdError,
  invalidAssetIdErrorFactory,
  ViewNotFoundError,
  type WorkspaceBase,
  type WorkspaceFrameBase,
  type WorkspaceFrameItem,
  WorkspacesService,
} from '@bizzdesign/data-access-workspaces';
import {
  AddAiContentToNewWorkspaceNavigationState,
  AddAiContentToWorkspaceNavigationState,
  AddViewToNewWorkspaceNavigationState,
  AddViewToWorkspaceNavigationState,
  AiContentNavigationState,
  CreateWorkspaceFrameNavigationState,
  DuplicateWorkspaceFrameNavigationState,
  ElementCollectionInNewWorkspaceNavigationState,
  ElementCollectionInWorkspaceNavigationState,
  ViewNavigationState,
  WorkspaceFrameNavigationState,
} from '@bizzdesign/feature-content-open';
import {
  type ElementCollection,
  TemplateType,
  assertIsTemplateType,
} from '@bizzdesign/shared/models';
import {
  FrameFactoryService,
  WorkspaceFactoryService,
} from '@bizzdesign/workspaces/feature-common';
import {
  EMPTY,
  type Observable,
  combineLatest,
  defer,
  map,
  mergeMap,
  of,
  skip,
  startWith,
  tap,
} from 'rxjs';

type CreateWorkspaceFrameBaseOptions = {
  readonly name?: string;
  readonly frameId?: UUID;
};

export class FrameContext {
  constructor(
    public subject:
      | FrameBase
      | FrameNotFoundError
      | FrameHasNoWorkspaceError
      | ViewNotFoundError
      | InvalidAssetIdError,
    public readonly autoRename: boolean = false,
  ) {}
}

export const frameContextResolver: ResolveFn<Observable<FrameContext>> = (
  route: ActivatedRouteSnapshot,
): Observable<Observable<FrameContext>> => {
  const action = route.paramMap.get('act');
  const frameId = route.paramMap.get('fid');
  const viewId = route.paramMap.get('vid');
  const router = inject(Router);
  const state = router.currentNavigation()?.extras.state;
  const workspaceFactoryService: WorkspaceFactoryService = inject(
    WorkspaceFactoryService,
  );
  const frameFactoryService = inject(FrameFactoryService);
  const workspacesService = inject(WorkspacesService);
  const aiAssistantV2Store = inject(AiAssistantV2Store);
  type SelectedAiContentState = {
    content: { id: string; configuration: object };
    dataset: UUID[];
  };

  const clearSelectedV5Insight = (): void => {
    aiAssistantV2Store.clearSelectedInsight();
  };

  const getContentStateFromV5SelectedInsight =
    (): Observable<SelectedAiContentState> =>
      defer(() => {
        const insight = aiAssistantV2Store.selectedInsight();
        if (insight === null) {
          return EMPTY;
        }
        const dataset = insight.elements.objects
          .map((element) => element.id)
          .filter((id): id is UUID => isUUID(id));
        return of({
          content: {
            id: insight.visualizationId,
            configuration: {
              visualizationId: insight.visualizationId,
              chartModifier: { mappingSpec: insight.mappingSpec },
            },
          },
          dataset,
        });
      });

  const getSelectedAiContentState = (): Observable<SelectedAiContentState> =>
    getContentStateFromV5SelectedInsight();

  const createWorkspaceFrameForConfiguration = <
    TWorkspace extends WorkspaceBase,
  >(
    name: string,
    workspace$: Observable<TWorkspace>,
    persistFrame: (
      frame: WorkspaceFrameBase,
      workspace: TWorkspace,
    ) => Observable<
      WorkspaceFrameBase | FrameNotFoundError | FrameHasNoWorkspaceError
    >,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return combineLatest([getSelectedAiContentState(), workspace$]).pipe(
      mergeMap(([{ content, dataset }, workspace]) => {
        assertIsTemplateType(content.id);
        return frameFactoryService
          .createWorkspaceFrameItem(workspace, content.id)
          .pipe(
            mergeMap((frameItem) =>
              frameFactoryService
                .createWorkspaceFrameForConfiguration(
                  name,
                  content.configuration,
                  dataset,
                  frameItem,
                )
                .pipe(
                  mergeMap((frame) =>
                    persistFrame(frame, workspace).pipe(
                      tap((persistedFrame) => {
                        if (
                          !(persistedFrame instanceof FrameNotFoundError) &&
                          !(persistedFrame instanceof FrameHasNoWorkspaceError)
                        ) {
                          clearSelectedV5Insight();
                        }
                      }),
                      // Keep the insight-clear tap above skip so it still runs.
                      skip(1),
                      startWith(frame),
                    ),
                  ),
                ),
            ),
          );
      }),
    );
  };

  const createAdhocFrameForConfiguration = (
    id: string,
    name: string,
  ): Observable<AdhocFrameBase> => {
    const frame = frameFactoryService.createAdhocFrameBase(id, name);

    return getSelectedAiContentState().pipe(
      map(({ content, dataset }) => {
        assertIsTemplateType(content.id);
        return frameFactoryService.createAdhocFrameForConfiguration(
          frame,
          content.configuration,
          dataset,
          content.id,
        );
      }),
      startWith(frame),
    );
  };

  const createWorkspaceFrameForConfigurationInWorkspace = (
    name: string,
    workspace: WorkspaceBase,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return createWorkspaceFrameForConfiguration(name, of(workspace), (frame) =>
      workspacesService.createWorkspaceFrame(frame, 'ai_content'),
    );
  };

  const createWorkspaceFrameForConfigurationInNewWorkspace = (
    name: string,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return createWorkspaceFrameForConfiguration(
      name,
      workspaceFactoryService.createWorkspace(),
      (frame, workspace) =>
        workspacesService.createWorkspaceFrameInNewWorkspace(
          frame,
          workspace,
          'ai_content',
        ),
    );
  };

  const createWorkspaceFrameForCollectionInWorkspace = (
    collection: ElementCollection,
    workspace: WorkspaceBase,
    templateType: TemplateType,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return frameFactoryService
      .createWorkspaceFrameItem(workspace, templateType)
      .pipe(
        mergeMap((frameItem) =>
          frameFactoryService
            .createWorkspaceFrameForElementCollection(
              collection,
              frameItem,
              templateType,
            )
            .pipe(
              mergeMap((frame) =>
                workspacesService
                  .createWorkspaceFrame(frame, 'element_collection')
                  .pipe(skip(1), startWith(frame)),
              ),
            ),
        ),
      );
  };

  const createWorkspaceFrameForCollectionInNewWorkspace = (
    collection: ElementCollection,
    templateType: TemplateType,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return workspaceFactoryService
      .createWorkspace()
      .pipe(
        mergeMap((workspace) =>
          frameFactoryService
            .createWorkspaceFrameItem(workspace, templateType)
            .pipe(
              mergeMap((frameItem) =>
                frameFactoryService
                  .createWorkspaceFrameForElementCollection(
                    collection,
                    frameItem,
                    templateType,
                  )
                  .pipe(
                    mergeMap((frame) =>
                      workspacesService
                        .createWorkspaceFrameInNewWorkspace(
                          frame,
                          workspace,
                          'element_collection',
                        )
                        .pipe(skip(1), startWith(frame)),
                    ),
                  ),
              ),
            ),
        ),
      );
  };

  const createWorkspaceFrameForViewInWorkspace = (
    viewId: string,
    workspace: WorkspaceBase,
  ): Observable<
    | WorkspaceFrameBase
    | FrameNotFoundError
    | FrameHasNoWorkspaceError
    | ViewNotFoundError
  > => {
    return frameFactoryService
      .createWorkspaceFrameItem(workspace, TemplateType.DESIGN)
      .pipe(
        mergeMap((frameItem) =>
          frameFactoryService
            .createWorkspaceFrameForView(viewId, frameItem)
            .pipe(
              mergeMap((frame) =>
                frame instanceof ViewNotFoundError
                  ? of(frame)
                  : workspacesService
                      .createWorkspaceFrame(frame, 'view')
                      .pipe(skip(1), startWith(frame)),
              ),
            ),
        ),
      );
  };

  const createWorkspaceFrameForViewInNewWorkspace = (
    viewId: string,
  ): Observable<
    | WorkspaceFrameBase
    | FrameNotFoundError
    | FrameHasNoWorkspaceError
    | ViewNotFoundError
  > => {
    return workspaceFactoryService
      .createWorkspace()
      .pipe(
        mergeMap((workspace) =>
          frameFactoryService
            .createWorkspaceFrameItem(workspace, TemplateType.DESIGN)
            .pipe(
              mergeMap((frameItem) =>
                frameFactoryService
                  .createWorkspaceFrameForView(viewId, frameItem)
                  .pipe(
                    mergeMap((frame) =>
                      frame instanceof ViewNotFoundError
                        ? of(frame)
                        : workspacesService
                            .createWorkspaceFrameInNewWorkspace(
                              frame,
                              workspace,
                              'view',
                            )
                            .pipe(skip(1), startWith(frame)),
                    ),
                  ),
              ),
            ),
        ),
      );
  };

  const duplicateWorkspaceFrameItem = (
    sourceFrame: WorkspaceFrameItem,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return frameFactoryService
      .duplicateWorkspaceFrameItem(sourceFrame)
      .pipe(
        mergeMap((duplicate) =>
          workspacesService
            .duplicateWorkspaceFrame(sourceFrame.id, duplicate)
            .pipe(startWith(duplicate)),
        ),
      );
  };

  const createWorkspaceFrameBase = (
    workspace: WorkspaceBase,
    options: CreateWorkspaceFrameBaseOptions = {},
  ): Observable<
    WorkspaceFrameBase | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return frameFactoryService
      .createWorkspaceFrameBase(workspace, options)
      .pipe(
        mergeMap((frame) =>
          workspacesService
            .createWorkspaceFrame(frame, 'manual')
            .pipe(skip(1), startWith(frame)),
        ),
      );
  };

  const createFrameForView = (
    viewId: string,
    viewName: string,
  ): Observable<FrameBase | ViewNotFoundError> => {
    const frame = frameFactoryService.createFrameBaseForView(viewId, viewName);
    return frameFactoryService
      .createFrameForView(viewId)
      .pipe(startWith(frame));
  };

  const getWorkspaceFrame = (
    frame: WorkspaceFrameItem,
  ): Observable<
    WorkspaceFrameItem | FrameNotFoundError | FrameHasNoWorkspaceError
  > => {
    return workspacesService.getWorkspaceFrame(frame.id).pipe(startWith(frame));
  };

  // A malformed route id must resolve to InvalidAssetIdError regardless of any
  // navigation state, so validate it before the state-specific branches below.
  if (action !== 'create') {
    if (frameId !== null && !isUUID(frameId)) {
      return of(of(new FrameContext(invalidAssetIdErrorFactory(frameId))));
    }
    if (frameId === null && viewId !== null && !isUUID(viewId)) {
      return of(of(new FrameContext(invalidAssetIdErrorFactory(viewId))));
    }
  }

  const autoRename =
    action === 'create' &&
    (state instanceof AddViewToWorkspaceNavigationState ||
      state instanceof AddViewToNewWorkspaceNavigationState ||
      (state instanceof CreateWorkspaceFrameNavigationState &&
        state.name === undefined));

  const result = (
    action === 'create'
      ? state instanceof AddAiContentToWorkspaceNavigationState
        ? createWorkspaceFrameForConfigurationInWorkspace(
            state.name,
            state.workspace,
          )
        : state instanceof AddAiContentToNewWorkspaceNavigationState
          ? createWorkspaceFrameForConfigurationInNewWorkspace(state.name)
          : state instanceof AddViewToWorkspaceNavigationState
            ? createWorkspaceFrameForViewInWorkspace(
                state.viewId,
                state.workspace,
              )
            : state instanceof AddViewToNewWorkspaceNavigationState
              ? createWorkspaceFrameForViewInNewWorkspace(state.viewId)
              : state instanceof ElementCollectionInWorkspaceNavigationState
                ? createWorkspaceFrameForCollectionInWorkspace(
                    state.elementCollection,
                    state.workspace,
                    state.templateType,
                  )
                : state instanceof
                    ElementCollectionInNewWorkspaceNavigationState
                  ? createWorkspaceFrameForCollectionInNewWorkspace(
                      state.elementCollection,
                      state.templateType,
                    )
                  : state instanceof CreateWorkspaceFrameNavigationState
                    ? createWorkspaceFrameBase(state.workspace, {
                        name: state.name,
                        frameId: state.frameId,
                      })
                    : state instanceof DuplicateWorkspaceFrameNavigationState
                      ? duplicateWorkspaceFrameItem(state.sourceFrame)
                      : EMPTY
      : frameId
        ? state instanceof AiContentNavigationState
          ? createAdhocFrameForConfiguration(state.id, state.name)
          : state instanceof WorkspaceFrameNavigationState
            ? getWorkspaceFrame(state.frame)
            : workspacesService.getWorkspaceFrame(frameId)
        : viewId
          ? state instanceof ViewNavigationState
            ? createFrameForView(state.viewId, state.viewName)
            : frameFactoryService.createFrameForView(viewId)
          : EMPTY
  ).pipe(map((subject) => new FrameContext(subject, autoRename)));

  return (frameId && state instanceof AiContentNavigationState) ||
    (action === 'create' && state instanceof AiContentNavigationState) ||
    (action === 'create' &&
      state instanceof DuplicateWorkspaceFrameNavigationState) ||
    (frameId && state instanceof WorkspaceFrameNavigationState) ||
    (viewId && state instanceof ViewNavigationState)
    ? of(result)
    : result.pipe(map((result) => of(result)));
};
